import { useEffect, useRef } from 'react';

type ApplianceChoice = { appliance: { id: string } };

/** A persisted null means automatic selection, not removal of equipment. */
export const effectiveSolarApplianceId = (
  pickedId: string | null | undefined,
  automatic: ApplianceChoice | undefined
) => pickedId ?? automatic?.appliance.id ?? null;

export const savedSolarPanelSelectionMatches = (
  savedId: string | null,
  pickedId: string | null | undefined,
  automatic: ApplianceChoice | undefined
) =>
  effectiveSolarApplianceId(savedId, automatic) ===
  effectiveSolarApplianceId(pickedId, automatic);

/** Keep the same choice during autosave/refetch, scoped to this design/source and selection. */
export const useAutomaticSolarEquipment = <T extends ApplianceChoice>(
  scope: string,
  pickedId: string | null | undefined,
  options: T[],
  pending: boolean
): T | undefined => {
  const retained = useRef<{
    scope: string;
    pickedId: string | null | undefined;
    choice: T | undefined;
  }>(undefined);
  let resolved: T | undefined;
  if (pickedId !== null)
    resolved = pickedId
      ? options.find(({ appliance }) => appliance.id === pickedId)
      : options[0];
  useEffect(() => {
    if (!pending) retained.current = { scope, pickedId, choice: resolved };
  }, [scope, pickedId, resolved, pending]);
  if (resolved || !pending) return resolved;
  return retained.current?.scope === scope &&
    retained.current.pickedId === pickedId
    ? retained.current.choice
    : undefined;
};
